import { IBundlePackage } from '@r1-frontend/shared/types/tariff/bundle/IBundlePackage';
import { IRecommendedPacket } from '@r1-frontend/shared/types/tariff/IRecommendedPacket';
import { IInternetPackage } from '@r1-frontend/shared/types/tariff/mono/IInternetPackage';

import {
    ISalePackageLinks,
    ISubscriptionsSummary,
    TSalePackage,
} from '~/src/api/common-types/sale-package';
import { TBooleanFromApi } from '~/src/api/common-types/TBooleanFromApi';
import { TConnectionPrice } from '~/src/features/full-buy/store/initialState';

import {
    getPropsFromDescription,
    getSpeedDescription,
    IAdditionalProduct,
    ISubscription,
    transformAdditionalProducts,
    transformSubscription,
} from './helpers/tariffTransformerHelpers';

// TODO рефакторинг, заменить все наименования свойств на camelCase
export interface IFullBuyTariff {
    isMono: boolean,
    isCottage: TBooleanFromApi,
    sale_package_id: number, // @todo избавиться от использования внешнего параметра в кач-ве выбранного элемента, завязаться на внутреннюю сущность (id)
    alias: string,
    title: string,
    tariff_name: string, // @todo избавиться от префикса, избыточен. Оставить name
    bgColor: string,
    colorText: string,
    image_background: string | null,
    image_background_webp: string | null,
    tariffLineAlias: string[],
    additionalProducts: IAdditionalProduct[],
    subscriptions: ISubscription[],
    sort_on_bundles: number | null,

    // price of tariff
    price: number, // @todo объединить все стоимость под одним родителем `{ prices: { main, promo, connection } }`, etc...
    // promo price of tariff
    promoPrice: number,
    // promo period of tariff
    promoPeriod: number,

    connectionPrice: {
        isDiscounted: boolean,
        base: number,
        discounted: number,

        // calculated cost value by the discount value, if true then will be a discounted otherwise will a base
        // created for the createAgreement method and the comment for a request
        final: number,
    },

    speed: number, // @todo объединить все скорость под одним родителем `{ speeds: { download, upload } }`
    available_on_address_restrict: boolean, // Флаг доступность тарифа на адресе с максимальной скоростью в 100 Мб/сек

    channelCount: number, // @todo объединить все количественные значения каналов под одним родителем `{ channels: { count, hdCount, sdCount } }`, etc...
    hdCount: number,
    sdCount: number,

    freePackets: number,
    downloadSpeed: string,
    recommendedPackets: IRecommendedPacket[],

    salePackageLinks: ISalePackageLinks[], // TODO доработать на беке?, возвращать данные по подпискам для текущего тарифа
    subscriptionsSummary: ISubscriptionsSummary[], // TODO доработать на беке, указать количество требуемых подписок для текущего тарифа, а не по всем
    tariffTags: string[],
    salePackageConnectionPrice: TConnectionPrice,
}

/**
 * Общие свойства для bundle и mono
 */
export const transformTariff = (tariffPackage: IBundlePackage | IInternetPackage, isSupportsWebp: boolean) => {
    const tariff_name_display = 'tariff_name_display' in tariffPackage ? tariffPackage.tariff_name_display : undefined;
    const description = getPropsFromDescription(tariffPackage.description, tariff_name_display);

    const {
        discountedConnectionPrice = 0,
        baseConnectionPrice = 0,
    } = tariffPackage.salePackageConnectionPrice || {};

    const hasDiscount = discountedConnectionPrice !== baseConnectionPrice && discountedConnectionPrice > 0;

    return {
        sale_package_id: tariffPackage.sale_package_id,
        tariff_name: tariffPackage.tariff_name,

        additionalProducts: transformAdditionalProducts(tariffPackage.additionalProducts),
        // If we have sale packages (also known as subscriptions or VASes), we will not need to show an offer contents.
        subscriptions: tariffPackage?.salePackageLinks?.length > 0 ? [] : transformSubscription(tariffPackage.offerContents, isSupportsWebp),

        downloadSpeed: getSpeedDescription(tariffPackage.advantages),

        salePackageLinks: tariffPackage.salePackageLinks,
        subscriptionsSummary: tariffPackage.subscriptionsSummary,

        connectionPrice: {
            isDiscounted: hasDiscount,
            base: baseConnectionPrice,
            discounted: discountedConnectionPrice,
            final: hasDiscount ? discountedConnectionPrice : baseConnectionPrice,
        },

        salePackageConnectionPrice: {
            base: tariffPackage.salePackageConnectionPrice?.baseConnectionPrice,
            withDiscount: tariffPackage.salePackageConnectionPrice?.discountedConnectionPrice,
        },

        // Флаг доступность тарифа на адресе с максимальной скоростью в 100 Мб/сек
        available_on_address_restrict: 'available_on_address_restrict' in tariffPackage
            ? Boolean(tariffPackage.available_on_address_restrict)
            : 'available_on_address_restrict' in tariffPackage.description
                ? Boolean(tariffPackage.description.available_on_address_restrict)
                : false,

        ...description,
    };
};


export const transformBundleTariff = (tariffPackage: IBundlePackage, isSupportsWebp: boolean): IFullBuyTariff => {
    const commonProps = transformTariff(tariffPackage, isSupportsWebp);
    return {
        ...commonProps,

        isMono: false,
        isCottage: tariffPackage.salePackage2in1.is_cottage,

        speed: tariffPackage.speed_int,

        price: tariffPackage.salePackage2in1.price_final,
        promoPrice: tariffPackage.salePackage2in1?.promo_price_final,
        promoPeriod: tariffPackage.salePackage2in1?.promo_duration_final,
        recommendedPackets: tariffPackage.salePackage2in1.recommendedPackets,
        channelCount: tariffPackage.channelCount,
        hdCount: tariffPackage.salePackage2in1.hd_count,
        sdCount: tariffPackage.salePackage2in1.sd_count,
        freePackets: tariffPackage.salePackage2in1.free_packets,
    };
};

export const transformInternetTariff = (tariffPackage: IInternetPackage, isSupportsWebp: boolean): IFullBuyTariff => {
    const commonProps = transformTariff(tariffPackage, isSupportsWebp);

    return {
        ...commonProps,

        isMono: true,
        isCottage: tariffPackage.is_cottage,

        speed: tariffPackage.speed,

        price: tariffPackage.price_final,
        promoPrice: tariffPackage.promo_price_final,
        promoPeriod: tariffPackage.promo_duration_final,
        recommendedPackets: tariffPackage.recommendedPackets,
        channelCount: 0,
        hdCount: 0,
        sdCount: 0,
        freePackets: tariffPackage.free_packets,
    };
};

/**
 * Возвращает только используемые в ПП свойства, сглаживает структуру тарифа
 * Трансформер должен вызываться на стороне браузера (не SSR), так как проверяется поддержка webp
 */
export const tariffTransformer = (tariffPackage: TSalePackage, isSupportsWebp: boolean): IFullBuyTariff => {
    return 'salePackage2in1' in tariffPackage
        ? transformBundleTariff(tariffPackage, isSupportsWebp)
        : transformInternetTariff(tariffPackage, isSupportsWebp);
};
